import React, { useCallback } from "react";
import { useEffect, lazy, useState, Suspense } from "react";
import { useDispatch, useSelector } from "react-redux";
import { MessageListProvider, useMessageList } from '../Context/MessageListContext';
import { DefaultRootState } from '@reduxLocal/persistState';
import { useMessages } from "../utils/useMessages";
import { useAllUsers } from "../utils/useAllUsers";
import { useOrganisationToggle } from "../utils/useOrganisationToggle";
import { useMessageModal } from "../utils/useMessageModal";
import { useSocketHandlers } from "../utils/useSocketHandlers";
import { useChatHistory } from "../utils/useChatHistory";
import { themeSelector } from "@reduxLocal/selectors/themeSelector";
import { UPDATE_CONVERSATION_ID } from "@reduxLocal/actions/types";
import useSound from '@utils/messagePing';
import noticeDispatcher from "@utils/noticeDispatcher";
import { parseJSON } from "jquery";
import { Text } from "tamagui";
import { useQueryClient } from '@tanstack/react-query';

const LazyMessages = lazy(() => import('../Components/LazyMessages'));

const MessageList = ({
  socket,
  userId,
  isUserOrganisation,
  setIsUserOrganisation,
  setData,
  clearBadge,
  selectedConversation,
  closeMessage,
  flipCard,
  handleOpen,
  handleSearch,
  setSearchVisible,
  companyMembers,  // Ensure these props are passed
  fullData,        // Ensure these props are passed
  itemMinWidth     // Stable minWidth for list items
}) => {
  const { dispatch: contextDispatch } = useMessageList();
  const [loading, setLoading] = useState(false);
  const playSound = useSound();
  const [aiSummary, setAiSummary] = useState("");
  const [messageArray, setMessageArray] = useState([]);
  const dispatch = useDispatch();
  const [conversationIdState, setConversationIdState] = useState("");
  const [query, setQuery] = useState("");
  const { state: { data } } = useMessageList();
  const messageModalDisplayed = useSelector((state: DefaultRootState) => state.modals.messageModal.modalDisplayed);
  const queryClient = useQueryClient();
  
  // Track active filter for refetching data when it changes
  const activeFilter = useSelector((state: DefaultRootState) => state.UI.messageAppFilter);
  const externalFilter = useSelector((state: DefaultRootState) => state.UI.messageExternalFilter);

  const { toggleSwitch } = useOrganisationToggle({ companyMembers, fullData, setIsUserOrganisation, setData });

  const { messageIdCallback, getMessagesById } = useMessages({
    setConversationIdState,
    setMessageArray,
    setAiSummary,
    userId,
  });

  // Fetch chat history at this level (same as useMessages) - stable across LazyMessages remounts
  useChatHistory();

  const { getAllUsers, refetch, isLoading } = useAllUsers({
    contextDispatch,
    setLoading
  });

  // The useQuery in useAllUsers will automatically fetch on mount with refetchOnMount: true
  // Keep getAllUsers as a fallback for mobile in case the query doesn't trigger properly
  useEffect(() => {
    // Call getAllUsers as a fallback to ensure data loads on mobile
    // The useQuery should handle this automatically, but this ensures it works on all platforms
    getAllUsers();
  }, []);

  // The useAllUsers hook already handles filter changes via its queryKey
  // React Query will automatically refetch when queries are invalidated with refetchType: 'active'

  // Wrapper for toggleSwitch to match expected signature
  const handleToggleSwitch = () => {
    toggleSwitch(!isUserOrganisation);
  };

  useMessageModal({
    handleOpen,
    closeMessage,
    setAiSummary
  });

  const handleClearBadge = useCallback((userId: string) => {
    if (!userId) return;

    contextDispatch({
      type: 'UPDATE_BADGE',
      userId,
      amount: -999,
      event: { convoId: selectedConversation?.conversationId },
    });

    queryClient.invalidateQueries({ queryKey: ['messageArray', selectedConversation?.conversationId || userId] });

    clearBadge?.(userId);
  }, [contextDispatch, clearBadge, queryClient, selectedConversation?.conversationId]);

  const { responseHandler, statusUpdateHandler, acknowledgeHandler, deliveredHandler } = useSocketHandlers({
    contextDispatch,
    selectedConversation,
    setMessageArray,
    playSound,
    clearBadge: handleClearBadge,
    messageModalDisplayed,
  });

  useEffect(() => {

    socket.on("join error", (e) => {
      console.log("Join error", e.message);
      if (__DEV__)
        noticeDispatcher({
          title: e.message || "Error",
          body: "Could not join messages. Please try again.",
          timestamp: Date.now(),
          type: e.type || "warning"
        })
    })

    socket.on("new user", (e) => {
      statusUpdateHandler(e);
    })

    socket.on("response", responseHandler);
    socket.on("message", responseHandler);
    socket.on('appendId', (id) => {
    });

    // Listen for new conversation creation and update specific user in cache
    const handleSetConvo = (data: { conversationId?: string; participants?: string[] }) => {
  
      if (!data?.conversationId || !data?.participants) {
        console.warn('⚠️ Missing conversationId or participants in "set convo" event');
        return;
      }

      // Find the other participant (not the current user)
      const otherParticipantId = data.participants.find(pId => pId !== userId?.toString());

      if (!otherParticipantId) {
        console.warn('⚠️ Could not find other participant in "set convo" event');
        // Fallback to invalidating the whole query
        queryClient.invalidateQueries({ queryKey: ['all-users'] });
        return;
      }

      // Update the specific user in the query cache (update for both filter states to ensure it works)
      [true, false].forEach((extFilter) => {
        queryClient.setQueryData(
          ['all-users', { filter: activeFilter, externalFilter: extFilter, page: 1, limit: 50 }],
          (oldData: any[] | undefined) => {
            if (!oldData || !Array.isArray(oldData)) {
              return oldData; // Skip if no data for this filter
            }

            // Find and update the user that matches the other participant
            const updatedData = oldData.map(user => {
              const userMatchId = user?._id?.toString() || user?.user?._id?.toString() || user?.id?.toString();
              if (userMatchId === otherParticipantId) {
                return {
                  ...user,
                  conversationId: data.conversationId
                };
              }
              return user;
            });

            return updatedData;
          }
        );
      });
    };
    socket.on('set convo', handleSetConvo);

    socket.on('joined', (e) => {
      if (__DEV__)
        noticeDispatcher({
          title: "Connected",
          body: "You are now connected to the message server.",
          timestamp: Date.now(),
          type: "success"
        })
    });

    return () => {
      socket.off("response", responseHandler);
      socket.off("new user", statusUpdateHandler);
      socket.off("joined");
      socket.off("appendId");
      socket.off("delivered", deliveredHandler);
      socket.off('set convo', handleSetConvo);
    };
  }, [socket, statusUpdateHandler, responseHandler, deliveredHandler]);

  return (
    <Suspense fallback={<Text>Loading...</Text>}>
      <LazyMessages
        flipCard={flipCard}
        query={query}
        isUserOrganisation={isUserOrganisation}
        clearBadge={handleClearBadge}
        messageIdCallback={messageIdCallback}
        setQuery={setQuery}
        openMessages={handleOpen}
        closeMessages={closeMessage}
        toggleSwitch={handleToggleSwitch}
        handleSearch={handleSearch}
        setSearchVisible={setSearchVisible}
        itemMinWidth={itemMinWidth}
        socket={socket}
      />
    </Suspense>
  );
};
export default MessageList;